import { ReactElement, useState, ChangeEvent, KeyboardEvent, MouseEvent } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Badge, Box, IconButton, Menu, MenuItem, TextField } from '@mui/material';
import LineAwesome from '../LineAwesome/LineAwesome';
import { LocationField } from '../LocationField/LocationField';
import { dsgvoService } from '../../services/dsgvo-service';
import { regionStore } from '../../store/jotai/store';
import { userAtom } from '../../store/jotai/atoms/user.atom';
import { authAtom } from '../../store/jotai/atoms/auth.atom';
import { basketAtom } from '../../store/jotai/atoms/basket.atom';
import { shippingRatesAtom } from '../../store/jotai/atoms/shipping-rates.atom';
import { WEBSHOP_ENABLED } from '../../constants/Settings';
import {
  totalUnreadAtom,
  conversationsAtom,
  activeConversationAtom,
  messagesAtom
} from '../../store/jotai/atoms/conversations.atom';
import { removeCookie } from '../../core/api/strapi-client';
import { AccountMenuItem, getAccountMenuItems } from '../Header/accountMenuItems';
import { SearchTypeSelect } from '../SearchTypeSelect/SearchTypeSelect';
import { getSearchPath, SearchType, SearchTypeValue } from '../../constants/SearchType';
import './NavIcons.scss';

interface NavIconsProps {
  onLinkClick: () => void;
  toggleCart: () => void;
  toggleBookmarks: () => void;
}

type OverlayType = '' | 'search' | 'location';

export const NavIcons = ({
  onLinkClick,
  toggleCart,
  toggleBookmarks
}: NavIconsProps): ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();

  const user = useAtomValue(userAtom, { store: regionStore });
  const basket = useAtomValue(basketAtom, { store: regionStore });
  const totalUnread = useAtomValue(totalUnreadAtom, { store: regionStore });

  const [overlayType, setOverlayType] = useState<OverlayType>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState<SearchTypeValue>(SearchType.All);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);

  const itemCount =
    basket?.basket_items?.flatMap((v) => v.items).reduce((sum, i) => sum + i.quantity, 0) ?? 0;
  const itemCountLabel = itemCount > 9 ? '9+' : itemCount;
  const unreadLabel = totalUnread > 9 ? '9+' : totalUnread;
  const accountMenuOpen = Boolean(accountMenuAnchor);

  const toggleOverlay = (type: Exclude<OverlayType, ''>) => {
    setOverlayType((current) => (current === type ? '' : type));
  };

  const handleSearch = (type: SearchTypeValue = searchType) => {
    setOverlayType('');
    history.push(getSearchPath(searchTerm, type));
  };

  // A new type re-runs a search that already has a term.
  const changeSearchType = (type: SearchTypeValue) => {
    setSearchType(type);
    if (searchTerm) {
      handleSearch(type);
    }
  };

  const openAccountMenu = (event: MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const closeAccountMenu = () => {
    setAccountMenuAnchor(null);
  };

  const navigateFromMenu = (to: string) => {
    closeAccountMenu();
    onLinkClick();
    history.push(to);
  };

  const handleDSGVOClick = () => {
    closeAccountMenu();
    dsgvoService.setCookieOverlayState(true);
  };

  const handleLogout = () => {
    regionStore.set(userAtom, null);
    regionStore.set(authAtom, 'unauthenticated');
    regionStore.set(basketAtom, null);
    regionStore.set(shippingRatesAtom, null);
    regionStore.set(conversationsAtom, { asUser: [], asVendor: [] });
    regionStore.set(activeConversationAtom, null);
    regionStore.set(messagesAtom, []);
    regionStore.set(totalUnreadAtom, 0);
    removeCookie();
    closeAccountMenu();
    onLinkClick();
    history.push('/');
  };

  const handleAccountItemClick = (item: AccountMenuItem) => {
    if (item.action === 'cookieSettings') {
      handleDSGVOClick();
    } else if (item.action === 'logout') {
      handleLogout();
    } else if (item.to) {
      navigateFromMenu(item.to);
    }
  };

  return (
    <Box className='nav-icons'>
      <Box className='nav-icons__bar'>
        <IconButton
          className='nav-icons__icon'
          onClick={() => toggleOverlay('search')}
          aria-label={t('navbar.search', 'Suche')}>
          <LineAwesome icon='search' />
        </IconButton>
        <IconButton
          className='nav-icons__icon'
          onClick={() => toggleOverlay('location')}
          aria-label={t('navbar.location', 'Standort')}>
          <LineAwesome icon='map-marker' />
        </IconButton>
        {user?.id && (
          <IconButton
            className='nav-icons__icon'
            onClick={toggleBookmarks}
            aria-label={t('navbar.bookmarks')}>
            <LineAwesome icon='heart' />
          </IconButton>
        )}
        {WEBSHOP_ENABLED && (
          <Badge
            color='primary'
            overlap='circular'
            badgeContent={itemCountLabel}
            invisible={itemCount === 0}>
            <IconButton
              className='nav-icons__icon'
              onClick={toggleCart}
              aria-label={t('navbar.cart', 'Warenkorb')}>
              <LineAwesome icon='shopping-cart' />
            </IconButton>
          </Badge>
        )}

        {user?.id ? (
          <>
            <Badge
              className='nav-icons__account-badge'
              color='error'
              overlap='circular'
              badgeContent={unreadLabel}
              invisible={totalUnread === 0}>
              <IconButton
                className='nav-icons__icon'
                onClick={openAccountMenu}
                aria-label={t('navbar.myAccount')}
                aria-haspopup='true'
                aria-expanded={accountMenuOpen}>
                <LineAwesome icon='user' />
              </IconButton>
            </Badge>
            <Menu
              className='nav-icons__account-menu'
              anchorEl={accountMenuAnchor}
              open={accountMenuOpen}
              onClose={closeAccountMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              {getAccountMenuItems(user).map((item) => (
                <MenuItem key={item.id} onClick={() => handleAccountItemClick(item)}>
                  <LineAwesome icon={item.icon} /> {item.label}
                  {item.showUnreadBadge && totalUnread > 0 && (
                    <Box component='span' className='nav-icons__menu-badge'>
                      {unreadLabel}
                    </Box>
                  )}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <IconButton
            className='nav-icons__icon'
            component={Link}
            to='/login'
            onClick={onLinkClick}
            aria-label={t('navbar.loginRegister')}>
            <LineAwesome icon='sign-in-alt' />
          </IconButton>
        )}
      </Box>

      {overlayType && (
        <Box className='nav-icons__overlay'>
          {overlayType === 'search' && (
            <Box className='nav-icons__overlay-row nav-icons__overlay-row--search'>
              <Box className='nav-icons__search'>
                <TextField
                  id='searchInput-mobile'
                  className='nav-icons__search-input'
                  type='text'
                  size='small'
                  fullWidth
                  placeholder={t('navbar.searchPlaceholder', 'Suchbegriff suchen...')}
                  value={searchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
                <LineAwesome
                  className='nav-icons__search-icon'
                  icon='search'
                  onClick={() => handleSearch()}
                />
                <SearchTypeSelect
                  value={searchType}
                  onChange={changeSearchType}
                  className='nav-icons__search-type'
                />
              </Box>
              <LineAwesome
                className='nav-icons__close'
                icon='times'
                onClick={() => toggleOverlay('search')}
              />
            </Box>
          )}
          {overlayType === 'location' && (
            <Box className='nav-icons__overlay-row'>
              <Box className='nav-icons__location'>
                <LocationField onHandleSelect={() => toggleOverlay('location')} />
              </Box>
              <LineAwesome
                className='nav-icons__close'
                icon='times'
                onClick={() => toggleOverlay('location')}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
